// Opt-in привязка к плавающему элементу сайта, в том числе внутри open Shadow DOM.
export function watchLauncherAnchor(
  root: HTMLElement,
  selector: string | null | undefined,
  restore: () => void,
): () => void {
  if (!selector) return () => {};

  const doc = root.ownerDocument;
  const win = doc.defaultView!;
  const steps = selector.split('>>>').map((step) => step.trim());
  let frame = 0;
  let retry = 0;
  let target: Element | null = null;
  const scopes = new Set<Document | ShadowRoot>();
  const schedule = () => {
    if (!frame) frame = win.requestAnimationFrame(update);
  };
  const mutations = new win.MutationObserver(schedule);
  const sizes = typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(schedule) : null;
  const observe = (scope: Document | ShadowRoot) => {
    scopes.add(scope);
    scope.addEventListener('transitionend', schedule, true);
    scope.addEventListener('transitioncancel', schedule, true);
    mutations.observe(scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  };
  const find = (): Element | null => {
    let scope: Document | ShadowRoot | null = doc;
    let element: Element | null = null;
    for (const step of steps) {
      if (!scope) return null;
      observe(scope);
      element = scope.querySelector(step);
      scope = element?.shadowRoot ?? null;
    }
    return element;
  };
  function update(): void {
    frame = 0;
    win.clearTimeout(retry);
    let anchor: Element | null;
    try {
      anchor = find();
    } catch {
      // Ошибочный/устаревший селектор не должен ломать чат на чужом сайте.
      restore();
      return;
    }
    if (anchor !== target) {
      sizes?.disconnect();
      if (anchor) sizes?.observe(anchor);
      target = anchor;
    }
    const rect = anchor?.getBoundingClientRect();
    const { clientWidth: width, clientHeight: height } = doc.documentElement;
    // attachShadow() не создаёт MutationRecord; ищем поздний shadowRoot до появления цели.
    if (!anchor) retry = win.setTimeout(schedule, 500);
    if (
      !rect ||
      rect.width <= 0 ||
      rect.height <= 0 ||
      rect.top < 68 ||
      rect.top >= height ||
      rect.left + rect.width / 2 < 28 ||
      rect.left + rect.width / 2 > width - 28 ||
      (anchor && win.getComputedStyle(anchor).visibility !== 'visible')
    ) {
      restore();
      return;
    }
    const right = width - rect.left - rect.width / 2 - 28;
    const bottom = height - rect.top + 12;
    root.style.left = 'auto';
    root.style.right = `${Math.max(0, right)}px`;
    root.style.bottom = `${bottom}px`;
    root.style.setProperty('--w-offset-bottom', `${bottom}px`);
  }

  update();
  win.addEventListener('resize', schedule);
  win.addEventListener('scroll', schedule, true);
  return () => {
    mutations.disconnect();
    sizes?.disconnect();
    win.cancelAnimationFrame(frame);
    win.clearTimeout(retry);
    win.removeEventListener('resize', schedule);
    win.removeEventListener('scroll', schedule, true);
    scopes.forEach((scope) => {
      scope.removeEventListener('transitionend', schedule, true);
      scope.removeEventListener('transitioncancel', schedule, true);
    });
  };
}
